import { z } from 'zod'
import { GlobalOperandTypeMeta, ConditionOperator, RightOperand, SingleComparison } from '../schema'

/**
 * What evaluation reads off a single comparison. The left operand is absent because it arrives as an
 * already-built resolver, which is what lets a subject with its own left operands share this path.
 */
export type EvaluableComparison = Pick<SingleComparison, 'operator' | 'secondaryField'>

export type OperandNodePath = string[]
export type LeftEvaluationTypeMeta = GlobalOperandTypeMeta | 'nodePath'
export type RightEvaluationTypeMeta = GlobalOperandTypeMeta

export type AnyEvaluationType = LeftEvaluationTypeMeta | RightEvaluationTypeMeta

export const isRightHandOperandEvaluationType = (value: AnyEvaluationType): value is RightEvaluationTypeMeta => {
  return value !== 'nodePath'
}

const ErrorEvaluationTypes = [
  'invalidOperator',
  'missingLeft',
  'missingRight',
  'invalidLeft',
  'invalidRight',
  'invalidTypes',
  'unknown',
] as const
export type ErrorEvaluationType = (typeof ErrorEvaluationTypes)[number]
export type ResolvedOperandDebug = { type: string; value: unknown } | { error: string; reason?: string }

export type ConditionEvaluationDebug = {
  operands: ResolvedOperandDebug[]
  operator: {
    configured: string | null
    executed: string | null
    migrated: boolean
  }
}

export type ConditionEvaluation = {
  type: 'success' | ErrorEvaluationType
  outcome: boolean
  debug?: ConditionEvaluationDebug
}

export type SingleConditionResult = {
  type: 'SINGLE'
  key: number
  outcome: boolean
  debug: ConditionEvaluationDebug
}

export type GroupConditionResult = {
  type: 'AND' | 'OR'
  key: number
  outcome: boolean
  items: ConditionTreeResult[]
}

export type ConstantConditionResult = {
  type: 'ALWAYS'
  key: number
  outcome: boolean
}

export type ConditionTreeResult = SingleConditionResult | GroupConditionResult | ConstantConditionResult

type OperandMetaToType = {
  string: string
  number: number
  boolean: boolean
  stringArray: string[]
  date: string // ISO 8601
  nodePath: OperandNodePath
}

export type ResolvedOperand<TypeMeta extends AnyEvaluationType> = {
  value: OperandMetaToType[TypeMeta]
  type: TypeMeta
  error: null
}

export const isOfResolvedType = <T extends AnyEvaluationType>(
  operand: ResolvedOperand<AnyEvaluationType>,
  type: T
): operand is ResolvedOperand<NoInfer<T>> => {
  return operand.type === type
}

export type ErrorResolution = { value: null; type: null; error: ErrorEvaluationType; reason?: string }
export const failResolution = (error: ErrorEvaluationType, reason?: string): ErrorResolution => ({
  value: null,
  type: null,
  error,
  reason,
})

export type MaybeResolved<T extends AnyEvaluationType> = ResolvedOperand<T> | ErrorResolution

export interface LeftEvaluationResolver {
  resolve: () => MaybeResolved<LeftEvaluationTypeMeta>
}

export interface RightEvaluationResolver {
  resolve: <T extends RightEvaluationTypeMeta>(type: T) => MaybeResolved<T>
  replace: (rhs: RightOperand) => RightEvaluationResolver
}

// Operators
export const EvaluationOnlyOperatorSchema = z.enum(['IS_WITHIN', 'IS_NOT_WITHIN', 'CONTAINS_EXACTLY', 'DOES_NOT_CONTAIN_EXACTLY'])
export type EvaluationOnlyOperator = z.infer<typeof EvaluationOnlyOperatorSchema>

export type EvaluationOperator = ConditionOperator | EvaluationOnlyOperator

const maybe =
  <T extends LeftEvaluationTypeMeta>(type: T) =>
  (value: OperandMetaToType[T] | null): ResolvedOperand<T> | null =>
    value != null ? { type, value, error: null } : null

export const maybeNumber = maybe('number')
export const maybeBoolean = maybe('boolean')
export const maybeString = maybe('string')
export const maybeStringArray = maybe('stringArray')
export const maybeNodePath = maybe('nodePath')
